<template>
  <div class="container"
    :class="{ 'hovered': hoveredCard, 'not-hovered': !hoveredCard && hovered > 0 && multiSample, 'multi-container': multiSample }">
    <div class="gene-block">
      <div class="gene" title="Gene">
        {{ props.alt.alteration ? props.alt.gene : 'FUSION' }}
      </div>
      <div title="Alteration">
        {{ props.alt.alteration || props.alt.gene }}
      </div>
    </div>
    <div v-if="multiSample" class="samples-block">
      <div class="sample-button" :class="{
        'sample-button-selected': alt.sample1,
        'sample-button-not-highlighted': alt.sample1 && ((hoveredCard && hovered !== 1) || (clicked > 0 && clicked !== 1))
      }">
        <span class="sample-button-label">S1</span>
        <span v-if="alt.sample1" class="material-symbols-outlined size-20">check</span>
      </div>
      <div class="sample-button" :class="{
        'sample-button-selected': alt.sample2,
        'sample-button-not-highlighted': alt.sample2 && ((hoveredCard && hovered !== 2) || (clicked > 0 && clicked !== 2))
      }">
        <span class="sample-button-label">S2</span>
        <span v-if="alt.sample2" class="material-symbols-outlined size-20">check</span>
      </div>
      <div v-if="sampleId.length === 3" class="sample-button" :class="{
        'sample-button-selected': alt.sample3,
        'sample-button-not-highlighted': alt.sample3 && ((hoveredCard && hovered !== 3) || (clicked > 0 && clicked !== 3))
      }">
        <span class="sample-button-label">S3</span>
        <span v-if="alt.sample3" class="material-symbols-outlined size-20">check</span>
      </div>
    </div>
    <div v-else class="vaf-block">
      <div title="VAF">
        {{ vaf }}
      </div>
    </div>
    <div class="trials-block">
      <span class="treatment" @click="onTreatmentsClicked"
        :class="{ 'selected': treatmentsSelected, 'treament-multi': multiSample }">
        <span class="material-symbols-outlined size-24 unchecked" :class="{ 'selected': treatmentsSelected }"
          title="Treatments">
          pill
        </span>
        <div class="count">
          {{ props.alt.treatmentsCount || 0 }}
        </div>
      </span>
      <span class="trial" @click="onTrialsClicked" :class="{ 'selected': trialsSelected }">
        <span class="material-symbols-outlined size-24 unchecked" :class="{ 'selected': trialsSelected }" title="Trials">
          medical_information
        </span>
        <div class="count">
          {{ props.alt.trialsCount || 0 }}
        </div>
      </span>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2024 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineProps, ref, watch } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const props = defineProps({
  alt: Object,
  hovered: Number,
  clicked: Number,
})

const treatmentsSelected = ref(false)
const trialsSelected = ref(false)

const genomicsResults = computed(() => store.getters?.session?.genomicsResults || {})
const multiSample = computed(() => store.getters?.session?.genomicsResults?.multiComparison || false)
const sampleId = computed(() => store.getters?.session?.genomicsResults?.sampleId || '')
const vaf = computed(() => {
  if (!isNaN(Number.parseFloat(props.alt.vaf))) {
    return `${(Number.parseFloat(props.alt.vaf) * 100 ).toFixed(1)}%`
  }
  return props.alt.vaf
})
const hoveredCard = computed(() => {
  return props.hovered === 1 && props.alt.sample1 || props.hovered === 2 && props.alt.sample2 || props.hovered === 3 && props.alt.sample3
})

const onTreatmentsClicked = () => {
  const cardState = {
    selectedAlteration: props.alt.originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alt.treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
}

const onTrialsClicked = () => {
  const cardState = {
    selectedAlteration: props.alt.originalAlt || '',
    treatmentOrTrialType: 'trials',
    treatmentOrTrialCount: props.alt.trialsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
}

watch(genomicsResults, () => {
  if (genomicsResults.value.selectedAlteration !== props.alt.originalAlt) {
    treatmentsSelected.value = false
    trialsSelected.value = false
    return
  }
  if (genomicsResults.value.treatmentOrTrialType === 'treatments') {
    treatmentsSelected.value = true
    trialsSelected.value = false
    return
  }
  treatmentsSelected.value = false
  trialsSelected.value = true
})
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  margin: 0.5rem 0.25rem 0.25rem 1rem;
  align-items: center;
  height: 3rem;
  width: fit-content;
  justify-content: flex-start;
  gap: 3vw;
}

.multi-container {
  gap: calc(3vw - 1.5rem);
}

.samples-block {
  display: flex;
  justify-content: center;
}

.sample-button-label {
  margin-left: 0.25rem;
}

.sample-button {
  display: flex;
  margin: 0.2rem;
  font-weight: bold;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
}

.sample-button-selected {
  background-color: var(--Secondary);
  border-radius: 15px;
  color: var(--White);
  font-weight: normal;
  padding: 0.05rem;
}

.sample-button-not-highlighted {
  background-color: var(--SecondaryLight);
}

.gene {
  font-size: 0.65rem;
  color: var(--Grey700);
  text-transform: uppercase;
}

.hovered {
  box-shadow: 0 2px 5px 1px var(--Grey500);
  border-radius: 3px;
}

.not-hovered {
  opacity: 0.33;
}

.gene-block {
  display: flex;
  flex-direction: column;
  margin-left: 0.5rem;
  text-align: left;
  width: 2.5rem;
  overflow-wrap: break-word;
}

.vaf-block {
  min-width: 2rem;
}

.selected {
  color: var(--Primary);
  font-weight: 700;
  font-style: bold;

  font-variation-settings:
    'FILL' 1,
    'wght' 400,
    'GRAD' 0,
    'opsz' 48;
}

.trials-block {
  display: flex;
  flex-direction: row;
  gap: calc(1.5vw);
  color: var(--Grey500);
  text-align: left;
  align-items: flex-start;
  justify-content: flex-start;
  margin-right: 0.2rem;
}

.treatment {
  display: flex;
  flex-direction: row;
  text-align: left;
  align-items: center;
  user-select: none;

  cursor: pointer;

  &:hover {
    color: var(--Grey700);
  }
}

.trial {
  display: flex;
  flex-direction: row;
  text-align: left;
  align-items: center;
  user-select: none;

  cursor: pointer;

  &:hover {
    color: var(--Grey700);
  }
}

.count {
  margin-left: 3px;
  min-width: 3ch;
}
</style>
